<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { opportunityMethods, usersMethods, clientMethods, leadMethods, crmOptionMethods } from "@/state/helpers";
import { CLIENT_MODULE } from "@/state/modules/client";
import { CRM_OPTION_MODULE } from "@/state/modules/crmOption";
import crmFormMixin from "@/mixins/crmForm.mixin";
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

// A deal belongs to an existing debtor OR a lead that hasn't converted yet.

export default {
  page() {
    return { title: this.isEdit ? i18n.t("opportunity.edit") : i18n.t("opportunity.create") };
  },
  components: { Layout, PageHeader, Multiselect },
  mixins: [crmFormMixin],
  data() {
    return {
      form: {
        title: "",
        description: "",
        debtor_code: null,
        lead_uuid: null,
        contact_first_name: "",
        contact_last_name: "",
        contact_phone: "",
        contact_email: "",
        stage: "prospecting",
        amount: 0,
        currency: "MYR",
        expected_close_date: "",
        assigned_to: null,
      },
      selectedDebtor: null,
      selectedLead: null,
      assignedUser: null,
      // Lines are edited on the quotation page; this form only needs to know
      // whether they exist, because the amount is derived from them.
      lineCount: 0,
    };
  },
  validations: {
    form: {
      title: { required },
      contact_first_name: { required },
      contact_last_name: { required },
      // Either phone or email — one contactable channel is enough.
      contact_phone: {
        requiredEither(value) {
          return !!(value && value.trim()) || !!(this.form.contact_email && this.form.contact_email.trim());
        },
      },
      contact_email: {
        email,
        requiredEither(value) {
          return !!(value && value.trim()) || !!(this.form.contact_phone && this.form.contact_phone.trim());
        },
      },
    },
  },
  computed: {
    ...mapState(CLIENT_MODULE, { clientList: "list" }),
    isEdit() {
      return !!this.$route.params.uuid;
    },
    title() {
      return this.isEdit ? i18n.t("opportunity.edit") : i18n.t("opportunity.create");
    },
    items() {
      return [
        { text: i18n.t("module.crm"), href: "/" },
        { text: i18n.t("opportunity.title"), href: "/opportunities" },
        { text: this.title, active: true },
      ];
    },
    userOptions() {
      return (this.$store.state.users.list.data || []).map((u) => ({ uuid: u.id, name: u.name }));
    },
    debtorOptions() {
      const items = this.clientList?.data || [];
      return items
        .map((c) => ({
          account_no: c.accountNo || c.account_no,
          company_name: c.companyName || c.company_name,
        }))
        .filter((c) => c.account_no);
    },    taxAmount() { return Math.round(this.linesTotal * this.taxRate) / 100; },

    leadOptions() {
      // Converted leads are customers now, so they are not offered — you would
      // pick the customer instead. The one this record already points at stays
      // in the list regardless: a won deal converts its own lead, and dropping
      // it from the options would blank the field and silently unlink it.
      return (this.$store.state.lead.list || [])
        .filter((l) => l.status !== "converted" || l.uuid === this.form.lead_uuid)
        .map((l) => ({
        uuid: l.uuid,
        label: l.company_name || l.contact_person || l.phone,
        contact_first_name: l.contact_first_name,
        contact_last_name: l.contact_last_name,
        contact_person: l.contact_person,
        phone: l.phone,
        email: l.email,
      }));
    },
    stageOptions() {
      return this.$store.getters[`${CRM_OPTION_MODULE}/selectable`]("deal_stages");
    },
    selectedStage() {
      return this.stageOptions.find((o) => o.value === this.form.stage) || null;
    },
  },
  async created() {
    await Promise.all([
      this.getAllUser({ filter: { pageNumber: 1, pageSize: 1000 } }),
      this.getAllClients({ filter: { pageNumber: 1, pageSize: 1000 } }),
      this.getAllLeads({ filter: { page: 1, size: 1000 } }),
      this.getCrmOptions(),
    ]);

    if (this.isEdit) {
      const r = await this.getOpportunity({ uuid: this.$route.params.uuid });
      const d = r?.data || {};
      Object.keys(this.form).forEach((k) => {
        if (d[k] !== undefined && d[k] !== null) this.form[k] = d[k];
      });
      // Rows created before the first/last split only carry contact_person.
      if (!d.contact_first_name && d.contact_person) {
        const parts = String(d.contact_person).trim().split(/\s+/);
        this.form.contact_first_name = parts.shift() || "";
        this.form.contact_last_name = parts.join(" ");
      }
      this.lineCount = (d.details || []).length;
      this.selectedDebtor = this.debtorOptions.find((c) => c.account_no === this.form.debtor_code) || null;
      this.selectedLead = this.leadOptions.find((l) => l.uuid === this.form.lead_uuid) || null;
      this.assignedUser = this.userOptions.find((u) => u.uuid === this.form.assigned_to) || null;
    }
  },
  methods: {
    ...opportunityMethods,
    ...usersMethods,
    ...clientMethods,
    ...leadMethods,
    ...crmOptionMethods,
    // Filling either half clears the pair's error on both inputs.
    onEitherInput() {
      this.onFieldInput("contact_phone");
      this.onFieldInput("contact_email");
    },
    // Picking a lead carries its contact details over, so the deal starts
    // pre-filled instead of making the agent retype what the lead already has.
    onLeadChange(l) {
      this.selectedLead = l;
      this.form.lead_uuid = l ? l.uuid : null;
      if (!l) return;
      if (!this.form.title) this.form.title = l.label || "";
      if (!this.form.contact_first_name && !this.form.contact_last_name) {
        if (l.contact_first_name || l.contact_last_name) {
          this.form.contact_first_name = l.contact_first_name || "";
          this.form.contact_last_name = l.contact_last_name || "";
        } else if (l.contact_person) {
          const parts = String(l.contact_person).trim().split(/\s+/);
          this.form.contact_first_name = parts.shift() || "";
          this.form.contact_last_name = parts.join(" ");
        }
      }
      if (!this.form.contact_phone && l.phone) this.form.contact_phone = l.phone;
      if (!this.form.contact_email && l.email) this.form.contact_email = l.email;
      ["contact_first_name", "contact_last_name", "contact_phone", "contact_email"].forEach((f) => this.onFieldInput(f));
    },
    onAssigneeChange(u) {
      this.assignedUser = u;
      this.form.assigned_to = u ? u.uuid : null;
    },
    onStageChange(o) {
      this.form.stage = o ? o.value : "prospecting";
    },    /** Picking a catalogue item fills the row in; everything stays editable. */
    onPickItem(line, item) {
      if (!item) { line.item_code = null; return; }
      line.item_code = item.item_code;
      if (!line.description) line.description = item.description || "";
      // Always reset: the previous item's unit is not valid for this one.
      line.uom = (item.uoms && item.uoms[0]) || item.uom || "";
      if (!Number(line.unit_price)) line.unit_price = Number(item.unit_price) || 0;
    },    fmtRm(v) {
      return Number(v || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async submit() {
      if (this.form.amount < 0) this.form.amount = 0;
      // Lines belong to the quotation page; omitting the key entirely leaves
      // whatever is already saved untouched.
      delete this.form.details;
      const payload = { ...this.form };
      if (!payload.expected_close_date) delete payload.expected_close_date;
      // Stage is owned by the stage/won/lost endpoints once the deal exists.
      if (this.isEdit) delete payload.stage;
      // debtor_code is left exactly as the server sent it. It is an outcome,
      // not a choice: winning a deal stamps the customer it became, and nulling
      // it here would erase that link the next time somebody edits the title.

      const result = await this.submitForm(
        () =>
          this.isEdit
            ? this.updateOpportunity({ uuid: this.$route.params.uuid, payload })
            : this.createOpportunity({ payload }),
        {
          successMessage: this.isEdit
            ? this.$t("message.updateXSuccess", [this.$t("opportunity.deal")])
            : this.$t("message.createXSuccess", [this.$t("opportunity.deal")]),
          errorContext: () => this.$store.state.opportunity.lastError,
        }
      );

      if (!result) return;

      const uuid = result?.data?.uuid || this.$route.params.uuid;
      if (!uuid) {
        this.$router.push({ name: "Opportunity-pipeline" });
        return;
      }

      // A new deal's next step is almost always pricing it, so go straight to
      // the quotation. Editing an existing one goes back to the deal, because
      // the reason for editing was something other than the lines.
      this.$router.push({
        name: this.isEdit ? "Opportunity-detail" : "Opportunity-quotation",
        params: { uuid },
      });
    },
    cancel() {
      this.$router.push({ name: "Opportunity-pipeline" });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('opportunity.dealTitle') + ' *'">
                    <b-form-input
                      v-model="form.title"
                      :class="{ 'is-invalid': isInvalid('title') }"
                      @input="onFieldInput('title')"
                    />
                    <div v-if="fieldError('title', $t('opportunity.dealTitle'))" class="invalid-feedback d-block">
                      {{ fieldError('title', $t('opportunity.dealTitle')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <!-- A deal is for someone who is not a customer yet; an existing
                     customer asking for a price is simply a quotation. The
                     customer below is shown, not chosen — it only exists because
                     winning this deal created one. -->
                <b-col cols="12" v-if="form.debtor_code">
                  <b-form-group :label="$t('opportunity.debtor')">
                    <div class="became-customer">
                      <i class="mdi mdi-account-check-outline mr-1"></i>
                      <strong>{{ form.debtor_code }}</strong>
                      <span v-if="selectedDebtor"> — {{ selectedDebtor.company_name }}</span>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.title')">
                    <multiselect
                      :value="selectedLead"
                      :options="leadOptions"
                      track-by="uuid"
                      label="label"
                      :show-labels="false"
                      :allow-empty="true"
                      open-direction="below"
                      :placeholder="$t('label.select')"
                      @input="onLeadChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('contact.firstName') + ' *'">
                    <b-form-input
                      v-model="form.contact_first_name"
                      :class="{ 'is-invalid': isInvalid('contact_first_name') }"
                      @input="onFieldInput('contact_first_name')"
                    />
                    <div v-if="fieldError('contact_first_name', $t('contact.firstName'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_first_name', $t('contact.firstName')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('contact.lastName') + ' *'">
                    <b-form-input
                      v-model="form.contact_last_name"
                      :class="{ 'is-invalid': isInvalid('contact_last_name') }"
                      @input="onFieldInput('contact_last_name')"
                    />
                    <div v-if="fieldError('contact_last_name', $t('contact.lastName'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_last_name', $t('contact.lastName')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.phone')">
                    <vue-tel-input
                      v-model="form.contact_phone"
                      mode="international"
                      default-country="MY"
                      :input-options="{ placeholder: '+60 12-345 6789', styleClasses: 'form-control' }"
                      valid-characters-only
                      class="tel-input-wrap"
                      :class="{ 'tel-invalid': isInvalid('contact_phone') }"
                      @input="onEitherInput"
                    />
                    <div v-if="fieldError('contact_phone', $t('lead.phone'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_phone', $t('lead.phone')) }}
                    </div>
                    <small v-else class="text-muted">{{ $t('message.requiredPhoneOrEmail') }}</small>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('label.email')">
                    <b-form-input
                      v-model="form.contact_email"
                      type="email"
                      :class="{ 'is-invalid': isInvalid('contact_email') }"
                      @input="onEitherInput"
                    />
                    <div v-if="fieldError('contact_email', $t('label.email'))" class="invalid-feedback d-block">
                      {{ fieldError('contact_email', $t('label.email')) }}
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12" v-if="!isEdit">
                  <b-form-group :label="$t('opportunity.stageLabel')">
                    <multiselect
                      :value="selectedStage"
                      :options="stageOptions"
                      track-by="value"
                      label="label"
                      :show-labels="false"
                      :allow-empty="false"
                      open-direction="below"
                      @input="onStageChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('opportunity.amount') + ' (RM)'">
                    <b-form-input v-model.number="form.amount" type="number" min="0" step="0.01" :disabled="lineCount > 0" />
                    <small v-if="lineCount" class="text-muted">{{ $t('opportunity.amountFromLines') }}</small>
                  </b-form-group>
                </b-col>

                <!-- What is actually being quoted. Optional: a deal can start as a
                     conversation and grow lines when there is something to price. -->
                <b-col cols="12">
                  <b-form-group>
                    <template #label>
                      {{ $t('opportunity.lines') }}
                      <b-button size="sm" variant="outline-primary" class="float-right" @click="addLine">
                        <i class="mdi mdi-plus"></i> {{ $t('opportunity.addLine') }}
                      </b-button>
                    </template>

                    <p v-if="!form.details.length" class="text-muted small mb-0">
                      {{ $t('opportunity.noLines') }}
                    </p>

                    <div v-for="(line, i) in form.details" :key="i" class="line-card">
                      <div class="d-flex align-items-center mb-2">
                        <span class="line-num">{{ i + 1 }}</span>
                        <span class="flex-grow-1"></span>
                        <a href="javascript:void(0);" class="text-danger" @click="removeLine(i)">
                          <i class="mdi mdi-delete"></i>
                        </a>
                      </div>
                      <div class="row">
                        <div class="col-12 mb-2">
                          <label class="small mb-1">{{ $t('opportunity.item') }}</label>
                          <multiselect
                            :value="selectedItem(line)"
                            :options="itemOptions"
                            track-by="item_code"
                            label="label"
                            :show-labels="false"
                            :allow-empty="true"
                            open-direction="below"
                            :placeholder="$t('opportunity.itemHelp')"
                            @input="(o) => onPickItem(line, o)"
                          />
                        </div>
                        <div class="col-12 mb-2">
                          <label class="small mb-1">{{ $t('opportunity.lineDescription') }} *</label>
                          <b-form-input v-model="line.description" size="sm" />
                        </div>
                        <div class="col-12 mb-2">
                          <label class="small mb-1">{{ $t('opportunity.qty') }}</label>
                          <b-form-input v-model.number="line.qty" type="number" min="0" step="0.01" size="sm" />
                        </div>
                        <div class="col-12 mb-2">
                          <label class="small mb-1">{{ $t('opportunity.uom') }}</label>
                          <b-form-input v-model="line.uom" size="sm" />
                        </div>
                        <div class="col-12 mb-2">
                          <label class="small mb-1">{{ $t('opportunity.unitPrice') }}</label>
                          <b-form-input v-model.number="line.unit_price" type="number" min="0" step="0.01" size="sm" />
                        </div>
                        <div class="col-12">
                          <div class="line-total">
                            {{ $t('opportunity.lineTotal') }}: <strong>{{ fmtRm(lineTotal(line)) }}</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="form.details.length" class="lines-total">
                      {{ $t('opportunity.amount') }} (RM): <strong>{{ fmtRm(linesTotal) }}</strong>
                    </div>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('opportunity.expectedCloseDate')">
                    <b-form-input v-model="form.expected_close_date" type="date" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('lead.assignee')">
                    <multiselect
                      :value="assignedUser"
                      :options="userOptions"
                      track-by="uuid"
                      label="name"
                      :show-labels="false"
                      :allow-empty="true"
                      open-direction="below"
                      :placeholder="$t('label.select')"
                      @input="onAssigneeChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group :label="$t('clientGroup.description')">
                    <b-form-textarea v-model="form.description" rows="3" />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right">
                <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
                <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.line-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  background: #fcfcfd;
}
.line-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: #7c4dff; color: #fff; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.line-total { text-align: right; font-size: 12px; color: #6c757d; }
.lines-total {
  text-align: right; padding: 10px 12px; border-radius: 8px;
  background: rgba(124, 77, 255, 0.08); color: #5b2ee0; font-size: 14px;
}

.lines-total {
  text-align: right; padding: 10px 12px; border-radius: 8px;
  background: rgba(124, 77, 255, 0.08); color: #5b2ee0; font-size: 13px; line-height: 1.7;
}
.lines-total .grand { font-size: 15px; }
.became-customer {
  padding: 9px 12px;
  border-radius: 8px;
  background: rgba(16, 185, 129, 0.10);
  color: #0b7a5b;
  font-size: 14px;
}
.tel-input-wrap >>> .vue-tel-input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background: #fff;
}
.tel-input-wrap >>> .vue-tel-input:focus-within {
  border-color: #7c4dff;
  box-shadow: 0 0 0 0.2rem rgba(124, 77, 255, 0.15);
}
.tel-input-wrap >>> .vti__input {
  border: none;
  border-radius: 0 0.25rem 0.25rem 0;
  padding: 0.375rem 0.75rem;
  font-size: 14px;
  color: #495057;
}
.tel-invalid >>> .vue-tel-input {
  border-color: #f46a6a;
}
</style>
